import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Plus, Edit2, Trash2, Users, Search, Clock, Archive, ArchiveRestore } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { getYearOptions, STREAMS } from '../utils/yearOptions';
import { normalizePGYear } from '../utils/yearOptions';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const BatchManagement = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false); // NEW: Toggle for archived batches
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTimeSlotModalOpen, setIsTimeSlotModalOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [selectedBatchForTimeSlots, setSelectedBatchForTimeSlots] = useState(null);
  const [timeSlotTemplates, setTimeSlotTemplates] = useState({});
  const [timeSlotConfig, setTimeSlotConfig] = useState({
    use_default_slots: true,
    custom_time_slots: []
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    stream: '',
    year: '',
    semester: '', // Can be comma-separated for multiple semesters
    students: [], // Array of {id, name, roll_number}
    time_slots: {
      monday: { start_time: '09:00', end_time: '18:00', enabled: true },
      tuesday: { start_time: '09:00', end_time: '18:00', enabled: true },
      wednesday: { start_time: '09:00', end_time: '18:00', enabled: true },
      thursday: { start_time: '09:00', end_time: '18:00', enabled: true },
      friday: { start_time: '09:00', end_time: '18:00', enabled: true },
      saturday: { start_time: '09:00', end_time: '18:00', enabled: true }
    }
  });
  
  // Student form state
  const [studentForm, setStudentForm] = useState({ 
    name: '', 
    enrollment_duration: '1 year',
    custom_duration: '' // For "Other" option
  });
  const [editingStudentIndex, setEditingStudentIndex] = useState(null);
  const [showStudentPopup, setShowStudentPopup] = useState(null); // Batch ID to show popup for
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 }); // Store popup position
  
  // Subject completion management
  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [selectedBatchForSubjects, setSelectedBatchForSubjects] = useState(null);
  const [allSubjects, setAllSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  
  // NEW: Archive management
  const [archivingBatch, setArchivingBatch] = useState(null); // Batch being archived
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false); // Archive confirmation dialog

  useEffect(() => {
    fetchBatches();
    fetchAllSubjects();
  }, [showArchived]); // Re-fetch when showArchived changes

  const fetchBatches = async () => {
    try {
      let response;
      if (showArchived) {
        // Fetch ONLY archived batches
        response = await axios.get(`${API}/batches/archived/list`);
      } else {
        // Fetch only active batches (default)
        response = await axios.get(`${API}/batches`, {
          params: { include_archived: false }
        });
      }
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSubjects = async () => {
    try {
      const response = await axios.get(`${API}/subjects`);
      setAllSubjects(response.data || []);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const handleManageSubjects = (batch) => {
    setSelectedBatchForSubjects(batch);
    setIsSubjectModalOpen(true);
  };

  const toggleSubjectCompletion = async (subjectId) => {
    if (!selectedBatchForSubjects) return;
    
    try {
      const response = await axios.post(
        `${API}/batches/${selectedBatchForSubjects.id}/toggle-subject-completion/${subjectId}`
      );
      
      toast.success(response.data.message);
      
      // Update local batch state
      setBatches(batches.map(b => {
        if (b.id === selectedBatchForSubjects.id) {
          const completedSubjects = b.completed_subjects || [];
          return {
            ...b,
            completed_subjects: response.data.is_completed
              ? [...completedSubjects, subjectId]
              : completedSubjects.filter(id => id !== subjectId)
          };
        }
        return b;
      }));
      
      // Update selected batch
      setSelectedBatchForSubjects({
        ...selectedBatchForSubjects,
        completed_subjects: response.data.is_completed
          ? [...(selectedBatchForSubjects.completed_subjects || []), subjectId]
          : (selectedBatchForSubjects.completed_subjects || []).filter(id => id !== subjectId)
      });
    } catch (error) {
      console.error('Error toggling subject completion:', error);
      toast.error('Failed to update subject status');
    }
  };

  // Normalize year labels for PG records (MBA + Fashion PG + Interior PG) so subjects/batches
  // saved with varied labels (SY, PG FY, PG First Year, Year 2 etc.) still match each other.
  // Uses semester (PG Sem 1-4) as the unambiguous PG signal.
  const normalizeMBAYear = (y) => {
    if (['Year 1', 'PG First Year', 'PG FY', 'FY'].includes(y)) return 'Year 1';
    if (['Year 2', 'PG Second Year', 'PG SY', 'SY'].includes(y)) return 'Year 2';
    return y;
  };

  const getFilteredSubjectsForBatch = (batch) => {
    if (!batch) return [];
    
    return allSubjects.filter(subject => {
      // Match stream
      if (batch.stream && subject.stream !== batch.stream) return false;
      
      // Match year using PG-aware normalization (works for MBA + Fashion PG + Interior PG).
      // For non-PG records, both sides pass through unchanged.
      if (batch.year) {
        const batchY = normalizePGYear(batch.year, batch.stream, batch.semester);
        const subjY = normalizePGYear(subject.year, subject.stream, subject.semester);
        // Also fall back to legacy MBA normalizer to bridge any other label-quirks
        const batchYFallback = batch.stream === 'MBA Design Management' ? normalizeMBAYear(batchY) : batchY;
        const subjYFallback = subject.stream === 'MBA Design Management' ? normalizeMBAYear(subjY) : subjY;
        if (subjYFallback !== batchYFallback) return false;
      }
      
      // Match semester
      if (batch.semester && subject.semester) {
        const batchSemesters = batch.semester.split(',').map(s => s.trim());
        if (!batchSemesters.includes(subject.semester)) return false;
      }
      
      return true;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBatch) {
        // Merge with existing batch data to preserve all fields
        const mergedData = {
          ...editingBatch,  // Start with all existing batch data
          ...formData,      // Override with form data
          id: editingBatch.id // Preserve ID
        };
        
        console.log('[UPDATE BATCH] Merged data being sent:', mergedData);
        
        await axios.put(`${API}/batches/${editingBatch.id}`, mergedData);
        toast.success('Batch updated successfully');
      } else {
        await axios.post(`${API}/batches`, formData);
        toast.success('Batch created successfully');
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchBatches();
    } catch (error) {
      console.error('Error saving batch:', error);
      toast.error('Failed to save batch');
    }
  };

  const handleEdit = (batch) => {
    console.log('[EDIT] Loading batch for editing:', batch);
    console.log('[EDIT] Batch time_slot_config:', batch.time_slot_config);
    setEditingBatch(batch);
    
    // Preserve all existing values - use actual batch values, not defaults
    const editFormData = {
      name: batch.name || '',
      description: batch.description || '',
      stream: batch.stream || '',
      year: batch.year || '',
      semester: batch.semester || '',
      students: batch.students || [], // Preserve students array
      time_slots: {}
    };
    
    // Carefully preserve time_slots - handle backend time_slot_config structure
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    days.forEach(day => {
      // Backend uses time_slot_config, not time_slots
      // For now, use default values - will need to fetch from /batches/{id}/time-slots if needed
      editFormData.time_slots[day] = {
        start_time: '09:00',
        end_time: '18:00',
        enabled: true
      };
    });
    
    // If batch has time_slot_config, try to extract default times
    if (batch.time_slot_config) {
      const defaultStart = batch.time_slot_config.default_start_time || '09:00';
      const defaultEnd = batch.time_slot_config.default_end_time || '18:00';
      
      days.forEach(day => {
        editFormData.time_slots[day] = {
          start_time: defaultStart,
          end_time: defaultEnd,
          enabled: true
        };
      });
    }
    
    console.log('[EDIT] Form data populated with time slots:', editFormData.time_slots);
    setFormData(editFormData);
    setIsModalOpen(true);
    
    // Fetch detailed time slots from the API
    fetchBatchTimeSlots(batch.id, editFormData);
  };

  // Fetch time slots for editing
  const fetchBatchTimeSlots = async (batchId, currentFormData) => {
    try {
      const response = await axios.get(`${API}/batches/${batchId}/time-slots`);
      const weeklySchedule = response.data.weekly_schedule;
      
      console.log('[EDIT] Fetched time slots from API:', weeklySchedule);
      
      // Update form data with actual time slots
      const updatedTimeSlots = {};
      Object.keys(weeklySchedule).forEach(day => {
        const dayData = weeklySchedule[day];
        updatedTimeSlots[day] = {
          start_time: dayData.start_time || '09:00',
          end_time: dayData.end_time || '18:00',
          enabled: dayData.enabled !== undefined ? dayData.enabled : true
        };
      });
      
      setFormData(prev => ({
        ...prev,
        time_slots: updatedTimeSlots
      }));
      
      console.log('[EDIT] Time slots updated in form:', updatedTimeSlots);
    } catch (error) {
      console.error('[EDIT] Error fetching batch time slots:', error);
      // Keep the default values if fetch fails
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this batch?')) {
      try {
        await axios.delete(`${API}/batches/${id}`);
        toast.success('Batch deleted successfully');
        fetchBatches();
      } catch (error) {
        console.error('Error deleting batch:', error);
        toast.error('Failed to delete batch');
      }
    }
  };

  // Archive Management Functions
  const handleArchiveClick = async (batch) => {
    setArchivingBatch(batch);
    setArchiveDialogOpen(true);
  };

  const handleArchive = async () => {
    try {
      const response = await axios.post(`${API}/batches/${archivingBatch.id}/archive`);
      toast.success(response.data.message || 'Batch archived successfully');
      setArchiveDialogOpen(false);
      setArchivingBatch(null);
      fetchBatches();
    } catch (error) {
      console.error('Error archiving batch:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to archive batch';
      toast.error(errorMsg);
      // Reset state on error so user can try again
      setArchiveDialogOpen(false);
      setArchivingBatch(null);
    }
  };

  const handleUnarchive = async (batchId, batchName) => {
    if (window.confirm(`Are you sure you want to unarchive "${batchName}"? This will make it visible in all regular views again.`)) {
      try {
        const response = await axios.post(`${API}/batches/${batchId}/unarchive`);
        toast.success(response.data.message || 'Batch unarchived successfully');
        fetchBatches();
      } catch (error) {
        console.error('Error unarchiving batch:', error);
        const errorMsg = error.response?.data?.detail || 'Failed to unarchive batch';
        toast.error(errorMsg);
      }
    }
  };




  // Student Management Functions
  const handleAddStudent = () => {
    if (!studentForm.name.trim()) {
      toast.error('Please enter student name');
      return;
    }
    
    const duration = studentForm.enrollment_duration === 'Other' 
      ? studentForm.custom_duration.trim() 
      : studentForm.enrollment_duration;
      
    if (!duration) {
      toast.error('Please specify enrollment duration');
      return;
    }
    
    const newStudent = {
      id: Date.now().toString(), // Temporary ID
      name: studentForm.name.trim(),
      enrollment_duration: duration
    };
    
    setFormData(prev => ({
      ...prev,
      students: [...(prev.students || []), newStudent]
    }));
    
    setStudentForm({ name: '', enrollment_duration: '1 year', custom_duration: '' });
    toast.success('Student added');
  };
  
  const handleEditStudent = (index) => {
    const student = formData.students[index];
    const predefinedOptions = ['3 months', '6 months', '1 year', '2 years', '3 years', '4 years'];
    const isCustom = !predefinedOptions.includes(student.enrollment_duration);
    
    setStudentForm({ 
      name: student.name, 
      enrollment_duration: isCustom ? 'Other' : student.enrollment_duration,
      custom_duration: isCustom ? student.enrollment_duration : ''
    });
    setEditingStudentIndex(index);
  };
  
  const handleUpdateStudent = () => {
    if (!studentForm.name.trim()) {
      toast.error('Please enter student name');
      return;
    }
    
    const duration = studentForm.enrollment_duration === 'Other' 
      ? studentForm.custom_duration.trim() 
      : studentForm.enrollment_duration;
      
    if (!duration) {
      toast.error('Please specify enrollment duration');
      return;
    }
    
    const updatedStudents = [...formData.students];
    updatedStudents[editingStudentIndex] = {
      ...updatedStudents[editingStudentIndex],
      name: studentForm.name.trim(),
      enrollment_duration: duration
    };
    
    setFormData(prev => ({
      ...prev,
      students: updatedStudents
    }));
    
    setStudentForm({ name: '', enrollment_duration: '1 year', custom_duration: '' });
    setEditingStudentIndex(null);
    toast.success('Student updated');
  };
  
  const handleDeleteStudent = (index) => {
    if (window.confirm('Remove this student from the batch?')) {
      const updatedStudents = formData.students.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        students: updatedStudents
      }));
      toast.success('Student removed');
    }
  };
  
  const handleCancelEditStudent = () => {
    setStudentForm({ name: '', enrollment_duration: '1 year', custom_duration: '' });
    setEditingStudentIndex(null);
  };

  const handleTimeSlotConfig = async (batch) => {
    setSelectedBatchForTimeSlots(batch);
    
    // Fetch existing time slot configuration
    try {
      const response = await axios.get(`${API}/batches/${batch.id}/time-slots`);
      const config = response.data.time_slot_config || { use_default_slots: true, custom_time_slots: [] };
      setTimeSlotConfig(config);
      
      // Fetch templates
      const templatesResponse = await axios.post(`${API}/time-slots/templates`);
      setTimeSlotTemplates(templatesResponse.data.templates);
      
      setIsTimeSlotModalOpen(true);
    } catch (error) {
      console.error('Error fetching time slot config:', error);
      toast.error('Failed to load time slot configuration');
    }
  };

  const handleSaveTimeSlotConfig = async () => {
    try {
      // Convert simplified config to the backend format
      let configToSave = { ...timeSlotConfig };
      
      if (!timeSlotConfig.use_default_slots && timeSlotConfig.selected_days && timeSlotConfig.start_time && timeSlotConfig.end_time) {
        // Create a single custom time slot that applies to all selected days
        configToSave.custom_time_slots = [{
          id: Date.now().toString(),
          name: `${timeSlotConfig.start_time}-${timeSlotConfig.end_time}`,
          start_time: timeSlotConfig.start_time,
          end_time: timeSlotConfig.end_time,
          duration_hours: timeSlotConfig.duration_hours || 1.5,
          is_active: true,
          selected_days: timeSlotConfig.selected_days
        }];
      }
      
      await axios.put(`${API}/batches/${selectedBatchForTimeSlots.id}/time-slots`, configToSave);
      toast.success('Time slot configuration updated successfully');
      setIsTimeSlotModalOpen(false);
      fetchBatches(); // Refresh batch list
    } catch (error) {
      console.error('Error saving time slot config:', error);
      toast.error('Failed to save time slot configuration');
    }
  };

  const addCustomTimeSlot = () => {
    const newSlot = {
      id: Date.now().toString(), // Temporary ID
      name: `Custom Slot ${timeSlotConfig.custom_time_slots.length + 1}`,
      start_time: '09:00',
      end_time: '10:00',
      duration_hours: 1.0,
      is_active: true
    };
    setTimeSlotConfig(prev => ({
      ...prev,
      custom_time_slots: [...prev.custom_time_slots, newSlot]
    }));
  };

  const removeCustomTimeSlot = (index) => {
    setTimeSlotConfig(prev => ({
      ...prev,
      custom_time_slots: prev.custom_time_slots.filter((_, i) => i !== index)
    }));
  };

  const updateCustomTimeSlot = (index, field, value) => {
    setTimeSlotConfig(prev => ({
      ...prev,
      custom_time_slots: prev.custom_time_slots.map((slot, i) => 
        i === index ? { ...slot, [field]: value } : slot
      )
    }));
  };

  const loadTemplate = (templateName) => {
    const template = timeSlotTemplates[templateName];
    if (template) {
      setTimeSlotConfig(prev => ({
        ...prev,
        use_default_slots: false,
        custom_time_slots: template.map((slot, index) => ({
          id: (Date.now() + index).toString(),
          ...slot,
          is_active: true
        }))
      }));
      toast.success(`Loaded ${templateName.replace('_', ' ')} template`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      stream: '',
      year: '',
      semester: '',
      time_slots: {
        monday: { start_time: '09:00', end_time: '18:00', enabled: true },
        tuesday: { start_time: '09:00', end_time: '18:00', enabled: true },
        wednesday: { start_time: '09:00', end_time: '18:00', enabled: true },
        thursday: { start_time: '09:00', end_time: '18:00', enabled: true },
        friday: { start_time: '09:00', end_time: '18:00', enabled: true },
        saturday: { start_time: '09:00', end_time: '18:00', enabled: true }
      }
    });
    setEditingBatch(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const filteredBatches = batches.filter(batch =>
    batch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (batch.description && batch.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Batch Management</h1>
          <p className="text-gray-600">Manage your student batches</p>
        </div>
        
        <Button onClick={openCreateModal} className="btn-primary flex items-center" data-testid="create-batch-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Batch
        </Button>
      </div>

      {/* Search and Archive Toggle */}
      <Card className="card-enhanced">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative flex-1 w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search batches..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 form-input"
                data-testid="search-batches"
              />
            </div>
            
            {/* Archive Toggle */}
            <Button 
              onClick={() => setShowArchived(!showArchived)}
              variant={showArchived ? "default" : "outline"}
              className="whitespace-nowrap"
            >
              {showArchived ? "Show Active Batches" : "Show Archived Batches"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Batches Grid */}
      {filteredBatches.length === 0 ? (
        <Card className="card-enhanced">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm 
                ? 'No batches found' 
                : showArchived 
                  ? 'No archived batches' 
                  : 'No batches yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm 
                ? 'Try adjusting your search terms' 
                : showArchived 
                  ? 'You haven\'t archived any batches yet' 
                  : 'Create your first batch to get started'}
            </p>
            {!searchTerm && !showArchived && (
              <Button onClick={openCreateModal} className="btn-primary" data-testid="empty-state-create-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create First Batch
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBatches.map((batch) => (
            <Card key={batch.id} className="card-enhanced hover-lift" data-testid={`batch-card-${batch.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold text-gray-900 mb-1">
                      {batch.name}
                    </CardTitle>
                    {batch.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {batch.description}
                      </p>
                    )}
                  </div>
                  <div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const buttonRect = e.currentTarget.getBoundingClientRect();
                        
                        // Calculate position ensuring popup stays within viewport
                        const viewportWidth = window.innerWidth;
                        const viewportHeight = window.innerHeight;
                        const popupWidth = 320; // 80 * 4 (w-80 in rem)
                        const popupMaxHeight = 400;
                        
                        let top = buttonRect.bottom + 8;
                        let left = buttonRect.left;
                        
                        // Adjust if popup would go off right edge
                        if (left + popupWidth > viewportWidth) {
                          left = viewportWidth - popupWidth - 16;
                        }
                        
                        // Adjust if popup would go off bottom edge
                        if (top + popupMaxHeight > viewportHeight) {
                          top = buttonRect.top - popupMaxHeight - 8;
                        }
                        
                        setPopupPosition({ top, left });
                        setShowStudentPopup(showStudentPopup === batch.id ? null : batch.id);
                      }}
                      className="p-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer"
                      title="Click to view students"
                    >
                      <div className="flex flex-col items-center">
                        <Users className="w-5 h-5 text-blue-600" />
                        <span className="text-xs text-blue-600 font-medium mt-1">
                          {batch.students?.length || 0}
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Created: {new Date(batch.created_at).toLocaleDateString()}
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleManageSubjects(batch)}
                      className="px-3 h-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                      data-testid={`manage-subjects-${batch.id}`}
                      title="Manage Subject Completion"
                    >
                      <span className="text-xs font-medium">✓ Subjects</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(batch)}
                      className="p-2 h-8 w-8"
                      data-testid={`edit-batch-${batch.id}`}
                      title="Edit Batch"
                    >
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTimeSlotConfig(batch)}
                      className="p-2 h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      data-testid={`config-time-slots-${batch.id}`}
                      title="Configure Time Slots"
                    >
                      <Clock className="w-3 h-3" />
                    </Button>
                    
                    {/* Archive/Unarchive Button */}
                    {batch.is_archived ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnarchive(batch.id, batch.name)}
                        className="p-2 h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                        data-testid={`unarchive-batch-${batch.id}`}
                        title="Unarchive Batch (Super Admin Only)"
                      >
                        <ArchiveRestore className="w-3 h-3" />
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleArchiveClick(batch)}
                        className="p-2 h-8 w-8 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                        data-testid={`archive-batch-${batch.id}`}
                        title="Archive Batch"
                      >
                        <Archive className="w-3 h-3" />
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(batch.id)}
                      className="p-2 h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-batch-${batch.id}`}
                      title="Delete Batch"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="batch-modal-title">
              {editingBatch ? 'Edit Batch' : 'Create New Batch'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Batch Name *
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
                placeholder="Enter batch name"
                required
                data-testid="batch-name-input"
              />
            </div>
            
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="form-input resize-none"
                rows="3"
                placeholder="Enter batch description (optional)"
                data-testid="batch-description-input"
              />
            </div>
            
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stream
              </label>
              <select
                value={formData.stream}
                onChange={(e) => setFormData({ ...formData, stream: e.target.value })}
                className="form-input"
                data-testid="batch-stream-select"
              >
                <option value="">Select Stream (Optional)</option>
                {STREAMS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year
                </label>
                <select
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  className="form-input"
                  data-testid="batch-year-select"
                  disabled={!formData.stream}
                >
                  <option value="">{formData.stream ? 'Select Year' : 'Select Stream first'}</option>
                  {getYearOptions(formData.stream).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Semester(s) (Optional) - Select multiple if needed
                </label>
                <div className="space-y-3">
                  {/* Quick selection buttons for common combinations */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, semester: 'Semester 1, Semester 2' })}
                    >
                      Sem 1 & 2
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, semester: 'Semester 3, Semester 4' })}
                    >
                      Sem 3 & 4
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, semester: 'Semester 5, Semester 6' })}
                    >
                      Sem 5 & 6
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, semester: 'Semester 7, Semester 8' })}
                    >
                      Sem 7 & 8
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, semester: 'PG Sem 1, PG Sem 2' })}
                    >
                      PG Sem 1 & 2
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, semester: 'PG Sem 3, PG Sem 4' })}
                    >
                      PG Sem 3 & 4
                    </Button>
                  </div>
                  
                  {/* Text input for custom semester selection */}
                  <Input
                    type="text"
                    placeholder="e.g., 'Semester 1, Semester 2' or 'PG Sem 1'"
                    value={formData.semester}
                    onChange={(e) => setFormData({ ...formData, semester: e.target.value })}
                    data-testid="batch-semester-select"
                  />
                  <p className="text-xs text-gray-500">
                    Enter semester names separated by commas for multiple semesters
                  </p>
                </div>
              </div>
            </div>

            {/* Students Section */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Students ({formData.students?.length || 0})
              </h3>
              <p className="text-sm text-gray-600">Add students to this batch</p>
              
              {/* Add/Edit Student Form */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Student Name *
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter student name"
                      value={studentForm.name}
                      onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Enrollment Duration *
                    </label>
                    <select
                      value={studentForm.enrollment_duration}
                      onChange={(e) => setStudentForm({ ...studentForm, enrollment_duration: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="3 months">3 months</option>
                      <option value="6 months">6 months</option>
                      <option value="1 year">1 year</option>
                      <option value="2 years">2 years</option>
                      <option value="3 years">3 years</option>
                      <option value="4 years">4 years</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                
                {/* Custom duration input for "Other" */}
                {studentForm.enrollment_duration === 'Other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Specify Duration *
                    </label>
                    <Input
                      type="text"
                      placeholder="e.g., 18 months, 5 years"
                      value={studentForm.custom_duration}
                      onChange={(e) => setStudentForm({ ...studentForm, custom_duration: e.target.value })}
                    />
                  </div>
                )}
                
                <div className="flex gap-2">
                  {editingStudentIndex !== null ? (
                    <>
                      <Button
                        type="button"
                        onClick={handleUpdateStudent}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Update Student
                      </Button>
                      <Button
                        type="button"
                        onClick={handleCancelEditStudent}
                        variant="outline"
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleAddStudent}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Student
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Students List */}
              {formData.students && formData.students.length > 0 && (
                <div className="bg-white border rounded-lg divide-y">
                  {formData.students.map((student, index) => (
                    <div key={student.id || index} className="p-3 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <p className="font-medium text-gray-900">{student.name}</p>
                        <p className="text-sm text-gray-500">Duration: {student.enrollment_duration}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditStudent(index)}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteStudent(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Time Slots Configuration Section */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-800">Weekly Time Slots</h3>
              <p className="text-sm text-gray-600">Configure the time slots for this batch (Monday to Saturday)</p>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="space-y-3">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(day => (
                    <div key={day} className="flex items-center space-x-3 p-3 bg-white rounded border">
                      <div className="w-24">
                        <input
                          type="checkbox"
                          id={`${day}_enabled`}
                          checked={formData.time_slots[day].enabled}
                          onChange={(e) => setFormData({
                            ...formData,
                            time_slots: {
                              ...formData.time_slots,
                              [day]: {
                                ...formData.time_slots[day],
                                enabled: e.target.checked
                              }
                            }
                          })}
                          className="w-4 h-4 text-blue-600 mr-2"
                          data-testid={`${day}-enabled-checkbox`}
                        />
                        <label htmlFor={`${day}_enabled`} className="text-sm font-medium capitalize">
                          {day}
                        </label>
                      </div>
                      
                      {formData.time_slots[day].enabled ? (
                        <>
                          <div className="flex-1">
                            <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                            <select
                              value={formData.time_slots[day].start_time}
                              onChange={(e) => setFormData({
                                ...formData,
                                time_slots: {
                                  ...formData.time_slots,
                                  [day]: {
                                    ...formData.time_slots[day],
                                    start_time: e.target.value
                                  }
                                }
                              })}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              data-testid={`${day}-start-time`}
                            >
                              <option value="09:00">9:00 AM</option>
                              <option value="09:30">9:30 AM</option>
                              <option value="10:00">10:00 AM</option>
                              <option value="10:30">10:30 AM</option>
                              <option value="11:00">11:00 AM</option>
                              <option value="11:30">11:30 AM</option>
                              <option value="12:00">12:00 PM</option>
                              <option value="12:30">12:30 PM</option>
                              <option value="13:00">1:00 PM</option>
                              <option value="13:30">1:30 PM</option>
                              <option value="14:00">2:00 PM</option>
                              <option value="14:30">2:30 PM</option>
                              <option value="15:00">3:00 PM</option>
                              <option value="15:30">3:30 PM</option>
                              <option value="16:00">4:00 PM</option>
                              <option value="16:30">4:30 PM</option>
                              <option value="17:00">5:00 PM</option>
                              <option value="17:30">5:30 PM</option>
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs text-gray-600 mb-1">End Time</label>
                            <select
                              value={formData.time_slots[day].end_time}
                              onChange={(e) => setFormData({
                                ...formData,
                                time_slots: {
                                  ...formData.time_slots,
                                  [day]: {
                                    ...formData.time_slots[day],
                                    end_time: e.target.value
                                  }
                                }
                              })}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              data-testid={`${day}-end-time`}
                            >
                              <option value="09:30">9:30 AM</option>
                              <option value="10:00">10:00 AM</option>
                              <option value="10:30">10:30 AM</option>
                              <option value="11:00">11:00 AM</option>
                              <option value="11:30">11:30 AM</option>
                              <option value="12:00">12:00 PM</option>
                              <option value="12:30">12:30 PM</option>
                              <option value="13:00">1:00 PM</option>
                              <option value="13:30">1:30 PM</option>
                              <option value="14:00">2:00 PM</option>
                              <option value="14:30">2:30 PM</option>
                              <option value="15:00">3:00 PM</option>
                              <option value="15:30">3:30 PM</option>
                              <option value="16:00">4:00 PM</option>
                              <option value="16:30">4:30 PM</option>
                              <option value="17:00">5:00 PM</option>
                              <option value="17:30">5:30 PM</option>
                              <option value="18:00">6:00 PM</option>
                            </select>
                          </div>
                        </>
                      ) : (
                        <span className="flex-1 text-sm text-gray-400 italic">Day not available</span>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  💡 Enable the days when this batch will have classes and set the time slots for each day.
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="flex-1"
                data-testid="cancel-batch-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-primary"
                data-testid="save-batch-btn"
              >
                {editingBatch ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Time Slot Configuration Modal */}
      <Dialog open={isTimeSlotModalOpen} onOpenChange={setIsTimeSlotModalOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Configure Time Slots for {selectedBatchForTimeSlots?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Default vs Custom Toggle */}
            <div className="space-y-4">
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="slot-type"
                    checked={timeSlotConfig.use_default_slots}
                    onChange={() => setTimeSlotConfig(prev => ({ 
                      ...prev, 
                      use_default_slots: true,
                      custom_time_slots: []
                    }))}
                  />
                  <span>Use Default 1.5-Hour Slots (9:00 AM - 6:00 PM)</span>
                </label>
              </div>
              
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="slot-type"
                    checked={!timeSlotConfig.use_default_slots}
                    onChange={() => setTimeSlotConfig(prev => ({ 
                      ...prev, 
                      use_default_slots: false
                    }))}
                  />
                  <span>Use Custom Time Slots</span>
                </label>
              </div>
            </div>

            {/* Templates Section */}
            {!timeSlotConfig.use_default_slots && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Quick Templates</h3>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    onClick={() => loadTemplate('1_hour_slots')}
                  >
                    1 Hour Slots (8 slots)
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    onClick={() => loadTemplate('2_hour_slots')}
                  >
                    2 Hour Workshops (4 slots)
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    onClick={() => loadTemplate('mixed_slots')}
                  >
                    Mixed Slots (1h, 1.5h, 2h)
                  </Button>
                </div>
              </div>
            )}

            {/* Simplified Time Configuration */}
            {!timeSlotConfig.use_default_slots && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium">Simple Time Configuration</h3>
                
                {/* Days Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Select Days</label>
                  <div className="grid grid-cols-7 gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                      <label key={day} className="flex flex-col items-center space-y-1">
                        <input
                          type="checkbox"
                          checked={(timeSlotConfig.selected_days || []).includes(day)}
                          onChange={(e) => {
                            const selectedDays = timeSlotConfig.selected_days || [];
                            const newSelectedDays = e.target.checked 
                              ? [...selectedDays, day]
                              : selectedDays.filter(d => d !== day);
                            setTimeSlotConfig(prev => ({ ...prev, selected_days: newSelectedDays }));
                          }}
                          className="rounded"
                        />
                        <span className="text-xs text-center">{day.slice(0, 3)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Single Time Slot for All Selected Days */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Time Slot (applies to all selected days)</label>
                  <div className="flex items-center space-x-4 p-4 border rounded-lg bg-blue-50">
                    <div className="flex items-center space-x-2">
                      <label className="text-sm">From:</label>
                      <Input
                        type="time"
                        value={timeSlotConfig.start_time || '09:00'}
                        onChange={(e) => {
                          const startTime = e.target.value;
                          setTimeSlotConfig(prev => ({ ...prev, start_time: startTime }));
                        }}
                        className="w-32"
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <label className="text-sm">To:</label>
                      <Input
                        type="time"
                        value={timeSlotConfig.end_time || '10:30'}
                        onChange={(e) => {
                          const endTime = e.target.value;
                          setTimeSlotConfig(prev => ({ ...prev, end_time: endTime }));
                          
                          // Calculate duration
                          const start = (timeSlotConfig.start_time || '09:00').split(':');
                          const end = endTime.split(':');
                          const startMinutes = parseInt(start[0]) * 60 + parseInt(start[1]);
                          const endMinutes = parseInt(end[0]) * 60 + parseInt(end[1]);
                          const durationHours = (endMinutes - startMinutes) / 60;
                          setTimeSlotConfig(prev => ({ ...prev, duration_hours: durationHours }));
                        }}
                        className="w-32"
                      />
                    </div>
                    
                    <div className="text-sm font-medium text-blue-700">
                      Duration: {timeSlotConfig.duration_hours?.toFixed(1) || '1.5'}h
                    </div>
                  </div>
                </div>

                {/* Manual Time Slot Entry Option */}
                <div className="border-t pt-4">
                  <label className="flex items-center space-x-2 mb-4">
                    <input
                      type="checkbox"
                      checked={timeSlotConfig.enable_manual_entry || false}
                      onChange={(e) => setTimeSlotConfig(prev => ({ 
                        ...prev, 
                        enable_manual_entry: e.target.checked 
                      }))}
                      className="rounded"
                    />
                    <span className="text-sm font-medium">Enable manual time slot entry for individual days</span>
                  </label>
                  
                  {timeSlotConfig.enable_manual_entry && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800 mb-3">
                        <strong>Note:</strong> With manual entry enabled, you can set different time slots for each selected day during schedule creation.
                      </p>
                      <div className="text-xs text-yellow-700">
                        This allows flexibility to have different timings on different days (e.g., 9-11 AM on Monday, 2-4 PM on Wednesday).
                      </div>
                    </div>
                  )}
                </div>

                {/* Preview */}
                {timeSlotConfig.selected_days && timeSlotConfig.selected_days.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-green-800 mb-2">Preview Configuration</h4>
                    <div className="text-sm text-green-700">
                      <p><strong>Days:</strong> {timeSlotConfig.selected_days.join(', ')}</p>
                      {!timeSlotConfig.enable_manual_entry ? (
                        <>
                          <p><strong>Time:</strong> {timeSlotConfig.start_time || '09:00'} - {timeSlotConfig.end_time || '10:30'}</p>
                          <p><strong>Duration:</strong> {timeSlotConfig.duration_hours?.toFixed(1) || '1.5'} hours per session</p>
                        </>
                      ) : (
                        <p><strong>Manual Entry:</strong> Individual time slots can be set for each day</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setIsTimeSlotModalOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="button"
                onClick={handleSaveTimeSlotConfig}
              >
                Save Configuration
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Student List Popup - Rendered via Portal for proper layering */}
      {showStudentPopup && batches.find(b => b.id === showStudentPopup)?.students?.length > 0 &&
        createPortal(
          <>
            {/* Backdrop with inline z-index for guaranteed layering */}
            <div
              className="fixed inset-0"
              style={{ 
                zIndex: 99998,
                backgroundColor: 'rgba(0, 0, 0, 0.2)'
              }}
              onClick={() => setShowStudentPopup(null)}
            />
            {/* Popup with inline z-index for guaranteed layering */}
            <div
              className="fixed w-80 bg-white border-2 border-gray-300 rounded-lg shadow-2xl"
              style={{
                top: `${popupPosition.top}px`,
                left: `${popupPosition.left}px`,
                maxHeight: '400px',
                zIndex: 99999
              }}
            >
              {(() => {
                const batch = batches.find(b => b.id === showStudentPopup);
                if (!batch) return null;
                return (
                  <>
                    <div className="p-3 border-b bg-gray-50">
                      <h3 className="font-semibold text-sm">Students in {batch.name}</h3>
                      <p className="text-xs text-gray-500">Total: {batch.students?.length || 0}</p>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {batch.students?.map((student, idx) => (
                        <div key={idx} className="p-3 border-b last:border-b-0 hover:bg-gray-50">
                          <p className="font-medium text-sm">{student.name}</p>
                          <p className="text-xs text-gray-600">Duration: {student.enrollment_duration}</p>
                        </div>
                      ))}
                    </div>
                    <div className="p-2 bg-gray-50 border-t flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowStudentPopup(null);
                          handleEdit(batch);
                        }}
                        className="flex-1 text-xs bg-blue-600 text-white py-1 px-2 rounded hover:bg-blue-700"
                      >
                        Edit Batch
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowStudentPopup(null);
                        }}
                        className="text-xs bg-gray-200 py-1 px-3 rounded hover:bg-gray-300"
                      >
                        Close
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </>,
          document.body
        )
      }

      {/* Subject Completion Management Modal */}
      <Dialog open={isSubjectModalOpen} onOpenChange={setIsSubjectModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Manage Subject Completion - {selectedBatchForSubjects?.name}
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-2">
              Mark subjects as completed to help identify remaining subjects during scheduling
            </p>
          </DialogHeader>

          {selectedBatchForSubjects && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-blue-900">
                  {selectedBatchForSubjects.completed_subjects?.length || 0} of{' '}
                  {getFilteredSubjectsForBatch(selectedBatchForSubjects).length} subjects completed
                </p>
              </div>

              {/* Subject List */}
              <div className="space-y-2">
                {getFilteredSubjectsForBatch(selectedBatchForSubjects).length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No subjects found for this batch. Subjects are filtered by stream, year, and semester.
                  </p>
                ) : (
                  getFilteredSubjectsForBatch(selectedBatchForSubjects).map(subject => {
                    const isCompleted = selectedBatchForSubjects.completed_subjects?.includes(subject.id);
                    
                    return (
                      <div
                        key={subject.id}
                        className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                          isCompleted
                            ? 'bg-green-50 border-green-300 hover:bg-green-100'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => toggleSubjectCompletion(subject.id)}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={isCompleted}
                            onChange={() => toggleSubjectCompletion(subject.id)}
                            className="w-5 h-5 text-green-600 rounded"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div>
                            <p className={`font-medium ${isCompleted ? 'text-green-700 line-through' : 'text-gray-900'}`}>
                              {subject.name}
                              {isCompleted && <span className="ml-2 text-xs">✅</span>}
                            </p>
                            <p className="text-xs text-gray-500">
                              {subject.year} • {subject.semester}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">Click to toggle</span>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end pt-4">
                <Button
                  onClick={() => setIsSubjectModalOpen(false)}
                  variant="outline"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-orange-600">
              <Archive className="w-5 h-5" />
              <span>Archive Batch</span>
            </DialogTitle>
          </DialogHeader>
          
          {archivingBatch && (
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-2">
                  You are about to archive <strong className="text-gray-900">"{archivingBatch.name}"</strong>
                </p>
                
                <div className="space-y-2 text-sm text-gray-600">
                  <p className="font-medium text-gray-800">This will:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Hide this batch from regular batch lists</li>
                    <li>Remove it from Smart Builder filters</li>
                    <li>Remove it from Smart Time Tables filters</li>
                    <li>Remove it from Session Management dropdowns</li>
                    <li>Prevent creating new sessions for this batch</li>
                  </ul>
                </div>
                
                <div className="mt-3 pt-3 border-t border-orange-200">
                  <p className="text-xs text-gray-500">
                    📌 The batch and all its data will be preserved and can be accessed in the "Archived Batches" view.
                  </p>
                  {archivingBatch.students && archivingBatch.students.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      👥 This batch has <strong>{archivingBatch.students.length} student(s)</strong>
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button
                  onClick={() => {
                    setArchiveDialogOpen(false);
                    setArchivingBatch(null);
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleArchive}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archive Batch
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default BatchManagement;